import "../style.css";
import { timeOptions } from "@/lib/utils/timezones";
import { type FormikHelpers, type FormikProps } from "formik";
import type { AssignmentTearsheetProps } from "@/react/prop-types";
import { ActionMode, ResourceType, WorkflowType } from "@/react/prop-types";
import {
   Page,
   H1,
   Box,
   Flex,
   Required,
   FlexList,
   Button,
   Form,
   Card,
   Title,
   useI18nContext,
   Spinner,
   H2,
   type FormView,
} from "@procore/core-react";
import type { FC } from "react";
import React, { useEffect, useState, useRef } from "react";
import { useAssignmentTearsheet } from "./assignment-tearsheet";

import { assignmentSchema } from "./validation-schemas";
import { useGroupContext } from "@/react/providers/group-context-provider";
import { useGetAssignmentCreationSupportDataQuery } from "./queries";
import { CustomFieldEntity } from "@laborchart-modules/common/dist/rethink/schemas/enums/custom-fields";
import { useGetCustomFields } from "../../common/queries/queries";
import type { WorkdayOptions } from "../../labor-plans/prop-types";
import {
   AuthAction,
   usePermissionContext,
   useViewPreferenceContext,
} from "@/react/providers/permission-context-provider";
import {
   formatOptions,
   formatProjectOptions,
   getProcoreApprovedPillColor,
   getProjectLabel,
   projectSearchComparator,
} from "@/react/shared/helper";
import { type AssignmentFormValues } from "./types";
import { Assignment2Store } from "@/stores/assignment-2-store.core";
import { useToastAlertContext } from "@procore/toast-alert";
import { ValidationErrorAlert, focusFirstInvalidField } from "@/react/shared/form-validation";
import {
   ALL_GROUPS_ID,
   DEFAULT_END_TIME,
   DEFAULT_START_TIME,
   workDays,
} from "@/react/shared/constants";
import { processCustomFieldsFormValues } from "@/react/shared/custom-field-utils";
import { DEFAULT_OVERTIME_RATES, DEFAULT_PAY_SPLIT_RATES, formInitialValues } from "./constants";
import type { GetAssignmentDetailReponse } from "@laborchart-modules/lc-core-api/dist/api/assignments";
import { DateUtils } from "@/lib/utils/date";
import type { Comments } from "../types";
import { DropDownType } from "../types";
import { useDropDownSelectionHandler } from "@/react/shared/hooks/useDropDownSelectionHandler";
import { reverseTransformOvertimeRates } from "../helper";
import { useProjectDateTimeDefaults } from "../useProjectDateTimeDefaults";
import type { CategoryTieredSelectOption } from "../helper";
import { useTieredCategorySelect } from "../shared/useTieredCategorySelect";
import { ScheduleCard } from "../shared/schedule-card";
import CustomFieldsCard from "../shared/custom-fields-card";
import { EquipmentSelectField } from "./equipment-select-field.lazy";
import { getProcoreCompanyIdFromURL } from "@/lib/utils/url";
import AdditionalInstructionsCard from "../shared/additional-instructions-card";
import CommentsCard from "../shared/comments-card";
import { authManager } from "@/lib/managers/auth-manager";
import { EquipmentAssignmentStore } from "@/stores/equipment-assignments-store.core";
import type { GetEquipmentAssignmentDetailResponse } from "@laborchart-modules/lc-core-api/dist/api/equipment-assignment/get-equipment-assignment";
import type { NotifiableError } from "@bugsnag/js";
import { reportError } from "@/utils/logging-utils";
import { submitAssignment } from "./submit-handler";
import type { CachedEquipment } from "@laborchart-modules/common/dist/postgres/schemas/common/custom-json";
import { CannedMessageType } from "@laborchart-modules/common/dist/postgres/schemas/common/enums";

export const getAssignmentActionText = (
   formValues: AssignmentFormValues,
   isCreateFlow: boolean,
   I18n: ReturnType<typeof useI18nContext>,
   workflowType: WorkflowType,
): string => {
   const { resource, equipment } = formValues;

   const resourceCount = Array.isArray(resource) ? resource.length : 0;
   const equipmentCount = Array.isArray(equipment) ? equipment.length : 0;
   const totalSelectedCount = resourceCount + equipmentCount;

   const createButtonText =
      workflowType === WorkflowType.SUBMIT_AND_ALERT
         ? I18n.t("views.company.workforce_planning.create_and_alert")
         : I18n.t("views.company.workforce_planning.create");
   const updateButtonText =
      workflowType === WorkflowType.SUBMIT_AND_ALERT
         ? I18n.t("views.company.workforce_planning.update_and_alert")
         : I18n.t("views.company.workforce_planning.update");

   const actionText = isCreateFlow ? createButtonText : updateButtonText;
   const countSuffix = totalSelectedCount > 1 ? ` (${totalSelectedCount})` : "";

   return `${actionText}${countSuffix}`;
};

export const AssignmentTearsheetContent: FC<AssignmentTearsheetProps> = ({
   onClose,
   showProjectDetail, // its an optional property so it can be undefined will be defined when create Assignment button is clicked from project detail page
   projectId, // its an optional property so it can be undefined will be defined when create Assignment button is clicked from project detail page
   categoryId,
   subcategoryId,
   assignmentId, // its an optional property so it can be undefined will be defined in the edit Assignment flow
   callback,
   resourceType, // optional property to determine the type of resource used in edit flow as we need to call different APIs for labor and equipment
   onCreateEditAssignment, // this will be used in create flow to create the assignment list after creating a new assignment
   people, // Optional property to pre-fill multiple people for multi-person assignments
   equipment, // Optional property to pre-fill equipment in drag-to-create flow
   startDate,
   endDate,
}) => {
   const isCreateFlow = !assignmentId; // main purpose of this flag is to determine whether the form is in create flow or edit flow
   const authorId = authManager.authedUserId();
   const { groupId } = useGroupContext();
   const { showToast } = useToastAlertContext();
   const I18n = useI18nContext();
   const { checkAuthAction } = usePermissionContext();
   const canSubmitAndAlert =
      checkAuthAction(AuthAction.MANAGE_ALERTS) && resourceType !== ResourceType.EQUIPMENT;
   const { dispatch } = useAssignmentTearsheet();
   const { data: assignmentCreationSupportData, isLoading: supportDataLoading } =
      useGetAssignmentCreationSupportDataQuery(groupId);
   const { data: customFields } = useGetCustomFields(CustomFieldEntity.ASSIGNMENT);
   const procoreCompanyId: string | undefined = getProcoreCompanyIdFromURL();
   const {
      currentAssignments,
      equipmentAssignmentsSchedules,
      handleSelection,
      projectProcoreId,
      totalHours,
      setTotalHours,
      projectEndDate,
      projectStartDate,
      projectWorkDays,
   } = useDropDownSelectionHandler(assignmentCreationSupportData);

   const [selectedWorkDays, setSelectedWorkDays] = useState<WorkdayOptions>(workDays);
   const [assignmentByTime, setAssignmentByTime] = useState(true);
   const [assignmentByAllocation, setAssignmentByAllocation] = useState(false);
   const [canViewAllStatuses, setCanViewAllStatuses] = useState<boolean>(false);
   const [isOverTimeSelected, setIsOverTimeSelected] = useState<boolean>(false);
   const [loading, setLoading] = useState<boolean>(false);
   const [filterIntegrationOnly, setFilterIntegrationOnly] = useState<boolean>(true);
   const [formViewMode, setFormViewMode] = useState<ActionMode>(ActionMode.CREATE);
   const [initialValues, setInitialValues] = useState<AssignmentFormValues>(
      formInitialValues(
         projectId,
         categoryId,
         subcategoryId,
         people,
         startDate,
         endDate,
         equipment,
      ),
   );
   const {
      options: tieredCategoryOptions,
      isLoading: tieredCategoryLoading,
      value: tieredCategoryValue,
      setValue: setTieredCategoryValue,
      handleChange: handleTieredCategoryChange,
      handleClear: handleTieredCategoryClear,
   } = useTieredCategorySelect(
      initialValues.project?.id ?? null,
      initialValues.subcategory?.id ?? initialValues.category?.id ?? null,
   );
   const [commentList, setCommentList] = useState<Comments[]>([]);
   const [makeTotalHoursAssignment, setMakeTotalHoursAssignment] = useState<boolean>(true);
   // Whether the send alerts modal should be opened
   const [shouldOpenSendAlerts, setShouldOpenSendAlerts] = useState<boolean>(false);
   // Use ref to track if we're in send alert workflow
   const isOpeningSendAlertsRef = useRef<boolean>(false);
   const { getViewPreference } = useViewPreferenceContext();
   const isLastNameFirst = getViewPreference()?.displayLastNamesFirst() ?? false;
   const getResourceName = (personName: { first?: string; last?: string }): string => {
      const { first = "", last = "" } = personName;
      return isLastNameFirst ? `${last}, ${first}`.trim() : `${first} ${last}`.trim();
   };
   const getOverTimeRates = () => {
      return assignmentCreationSupportData?.data.overtime_day_rates ?? DEFAULT_OVERTIME_RATES;
   };
   // Helper function to map data to initial values for the assignment form, this will be triggered in edit mode
   const mapCommonAssignmentDataToInitialValues = (data: any) => {
      return {
         project: {
            id: data.project?.id,
            // The details response carries the project's job number, so the label renders
            // even when the project is absent from the dropdown options
            label: data.project
               ? getProjectLabel(data.project.name, data.project.job_number, I18n)
               : "",
         },
         status: data.status
            ? {
                 id: data.status.id,
                 label: data.status.name,
                 color: getProcoreApprovedPillColor(data.status.color),
                 sequence: data.status.sequence,
              }
            : null,
         category: data.category
            ? {
                 id: data.category.id,
                 label: data.category.name,
              }
            : null,
         subcategory: data.subcategory
            ? {
                 id: data.subcategory.id,
                 label: data.subcategory.name,
              }
            : null,
         start_date: DateUtils.getAttachedDate(data.start_day),
         end_date: DateUtils.getAttachedDate(data.end_day),
         start_time: {
            id: data.start_time ?? DEFAULT_START_TIME,
            label: data.start_time
               ? timeOptions.find(({ id }) => id === data.start_time)!.label
               : timeOptions.find(({ id }) => id === DEFAULT_START_TIME)!.label,
         },
         end_time: {
            id: data.end_time ?? DEFAULT_END_TIME,
            label: data.end_time
               ? timeOptions.find(({ id }) => id === data.end_time)!.label
               : timeOptions.find(({ id }) => id === DEFAULT_END_TIME)!.label,
         },
         assignment_by_time: data.start_time != null && data.end_time != null,
         assignment_by_allocation: Boolean(data.percent_allocated),
         work_days: data.work_days,
         percent_allocated: data.percent_allocated ?? 100,
         overtime: data.overtime,
         pay_split: data.pay_split ?? DEFAULT_PAY_SPLIT_RATES,
         overtime_rates: data.overtime_rates
            ? reverseTransformOvertimeRates(data.overtime_rates)
            : getOverTimeRates(),
         work_scope_text: data.work_scope_text ?? "",
         instruction_text: data.instruction_text ?? "",
         comments: "",
      };
   };

   const mapLaborAssignmentDataToInitialValues = (
      assignmentDetails: GetAssignmentDetailReponse,
   ) => {
      const { data } = assignmentDetails;
      const baseFormValues = {
         ...mapCommonAssignmentDataToInitialValues(data),
         resource: data.person
            ? {
                 id: data.person.id,
                 label: getResourceName(data.person.name),
              }
            : [],
         equipment: null,
         weeks: assignmentCreationSupportData?.data.company_tbd_weeks ?? 26,
      };

      if (projectId) {
         baseFormValues.project.id = projectId;
      }

      // Override category fields if provided as props (e.g., from drag and drop)
      // undefined = use DB data, null = explicitly no category, string = use this category
      if (categoryId !== undefined) {
         baseFormValues.category = categoryId ? { id: categoryId, label: "" } : null;
      }
      if (subcategoryId !== undefined) {
         baseFormValues.subcategory = subcategoryId ? { id: subcategoryId, label: "" } : null;
      }

      setLocalStates(baseFormValues, data);
      return { ...baseFormValues, ...processCustomFieldsFormValues(data.custom_fields) };
   };

   const mapEquipmentAssignmentDataToInitialValues = (
      assignmentDetails: GetEquipmentAssignmentDetailResponse,
   ) => {
      const { data } = assignmentDetails;
      const baseFormValues = {
         ...mapCommonAssignmentDataToInitialValues(data),
         equipment: {
            id: data.resource_id,
            name: data.equipment ? (data.equipment as CachedEquipment).identification_number : "", // here mapping name to identification_number to show the identification number for selected equipment to be in sync with the new option types for equipment dropdown
            identificationNumber: "", // required by type
         },
         resource: null,
         weeks: assignmentCreationSupportData?.data.company_tbd_weeks ?? 26,
      };

      // Override category fields if provided as props (e.g., from drag and drop)
      // undefined = use DB data, null = explicitly no category, string = use this category
      if (categoryId !== undefined) {
         baseFormValues.category = categoryId ? { id: categoryId, label: "" } : null;
      }
      if (subcategoryId !== undefined) {
         baseFormValues.subcategory = subcategoryId ? { id: subcategoryId, label: "" } : null;
      }

      setLocalStates(baseFormValues, data);
      return baseFormValues; // No custom fields for equipment assignment yet
   };

   const setLocalStates = (formValues: AssignmentFormValues, data: any) => {
      setAssignmentByAllocation(formValues.assignment_by_allocation);
      setAssignmentByTime(formValues.assignment_by_time);
      setSelectedWorkDays(formValues.work_days);
      setIsOverTimeSelected(formValues.overtime);
      setTotalHours(
         formValues.pay_split.straight +
            formValues.pay_split.overtime +
            formValues.pay_split.unpaid,
      );
      setFormViewMode(isCreateFlow ? ActionMode.READ : ActionMode.UPDATE);
      setFilterIntegrationOnly(false);
      setCommentList(data.comments ?? []);
      setMakeTotalHoursAssignment(false);
   };

   useProjectDateTimeDefaults({
      projectStartDate,
      projectEndDate,
      // The selected project, so times follow dropdown changes as well as a preset project
      projectId: initialValues.project?.id,
      isCreateFlow,
      setInitialValues,
      supportData: assignmentCreationSupportData,
      parentStartDate: startDate,
      parentEndDate: endDate,
   });

   useEffect(() => {
      const fetchAssignmentDetails = async () => {
         if (assignmentId && resourceType) {
            try {
               if (resourceType === ResourceType.EQUIPMENT) {
                  const equipmentAssignmentDetails: GetEquipmentAssignmentDetailResponse =
                     await EquipmentAssignmentStore.getEquipmentAssignmentDetails(assignmentId)
                        .payload;
                  setInitialValues(
                     mapEquipmentAssignmentDataToInitialValues(equipmentAssignmentDetails),
                  );
               } else {
                  const assignmentDetails: GetAssignmentDetailReponse =
                     await Assignment2Store.getAssignmentDetails(assignmentId).payload;
                  setInitialValues(mapLaborAssignmentDataToInitialValues(assignmentDetails));
               }
            } catch (error) {
               showToast.error(I18n.t("views.company.workforce_planning.error"));
            }
         }
      };
      fetchAssignmentDetails();
   }, [assignmentId, resourceType]);

   // this is needed to toggle project-level options based on the project selection
   useEffect(() => {
      if (initialValues.project) {
         handleSelection(initialValues.project, DropDownType.PROJECT, makeTotalHoursAssignment);
      }
   }, [initialValues.project, assignmentCreationSupportData?.data]);

   // New assignments start with the selected project's work days.
   useEffect(() => {
      if (!isCreateFlow) return;
      const work_days = projectWorkDays ?? workDays;
      setSelectedWorkDays(work_days);
      setInitialValues((prevValues) => ({ ...prevValues, work_days }));
   }, [projectWorkDays, isCreateFlow, setSelectedWorkDays]);

   // written for population of overtime rates when the form is opened in create mode
   useEffect(() => {
      setInitialValues((prevValues) => {
         const isDefaultRates =
            JSON.stringify(prevValues.overtime_rates) === JSON.stringify(DEFAULT_OVERTIME_RATES);
         if (isDefaultRates) {
            return {
               ...prevValues,
               overtime_rates: getOverTimeRates(),
               weeks: assignmentCreationSupportData?.data.company_tbd_weeks,
            };
         }

         return prevValues;
      });
   }, [assignmentCreationSupportData?.data]);

   // written for population of pay split rates when the form is opened in create mode
   useEffect(() => {
      setInitialValues((prevValues) => {
         const isDefaultPaySplit =
            JSON.stringify(prevValues.pay_split) === JSON.stringify(DEFAULT_PAY_SPLIT_RATES);
         if (isDefaultPaySplit) {
            return {
               ...prevValues,
               pay_split: {
                  overtime: 0,
                  straight: totalHours,
                  unpaid: 0,
               },
            };
         }

         return prevValues;
      });
   }, [totalHours]);
   const handleClose = () => {
      // this will navigate to project details page on cancel click
      if (projectId && showProjectDetail) {
         showProjectDetail(projectId);
      }
      // this will simply close the tearsheet
      else {
         onClose();
      }
   };

   useEffect(() => {
      const canViewAllStatuses = checkAuthAction(AuthAction.CAN_VIEW_ALL_STATUSES);
      setCanViewAllStatuses(canViewAllStatuses);
   }, [checkAuthAction]);

   const validationSchema = assignmentSchema(
      I18n,
      !canViewAllStatuses,
      selectedWorkDays,
      assignmentByAllocation,
      assignmentByTime,
      projectProcoreId !== null,
      true, // Equipment is always enabled
      isOverTimeSelected,
      isCreateFlow,
      projectStartDate,
      projectEndDate,
   );

   /* istanbul ignore next */
   async function handleSubmit(
      values: AssignmentFormValues,
      { resetForm }: FormikHelpers<AssignmentFormValues>,
   ) {
      try {
         setLoading(true);
         const { laborResult, equipmentResult } = await submitAssignment(values, {
            authorId: authorId ?? "",
            customFields: customFields ?? null,
            isCreateFlow,
            assignmentId,
            projectProcoreId,
            procoreCompanyId,
         });

         // Pass raw API data to the callback — transformation to Bryntum shapes happens in gantt-container
         if (callback) {
            const rawLaborAssignments = laborResult?.data
               ? Array.isArray(laborResult.data)
                  ? laborResult.data
                  : [laborResult.data]
               : [];
            const rawEquipmentAssignments = equipmentResult?.data
               ? Array.isArray(equipmentResult.data)
                  ? equipmentResult.data
                  : [equipmentResult.data]
               : [];
            if (rawLaborAssignments.length > 0 || rawEquipmentAssignments.length > 0) {
               callback({
                  assignments: rawLaborAssignments,
                  equipmentAssignments: rawEquipmentAssignments,
               });
            }
         }

         // the sole intention of this callback is to refresh the assignment list
         if (onCreateEditAssignment) {
            const assignment = laborResult?.data ?? equipmentResult?.data;
            if (assignment) {
               if (!isCreateFlow && "id" in assignment) {
                  onCreateEditAssignment(assignment.id);
               } else if (Array.isArray(assignment) && assignment.length > 0) {
                  onCreateEditAssignment(assignment[0].id);
               }
            }
         }
         if (isOpeningSendAlertsRef.current && values.project?.id && laborResult?.data) {
            const assignmentsData = Array.isArray(laborResult.data)
               ? laborResult.data
               : [laborResult.data];
            dispatch({
               type: "open-send-assignment-alerts",
               projectId: values.project.id,
               assignments: assignmentsData.map((a: any) => ({
                  id: a.id,
                  recipientIds: [a.resource_id],
               })),
               alertType: isCreateFlow
                  ? CannedMessageType.ASSIGNMENT_NEW
                  : CannedMessageType.ASSIGNMENT_EDIT,
            });
         } else if (!isCreateFlow) {
            // If editing and not opening alerts, switch to READ mode
            setFormViewMode(ActionMode.READ);
         } else if (!shouldOpenSendAlerts) {
            // Only close the form in create flow if we're NOT opening send alerts
            if (callback) {
               onClose();
            } else {
               handleClose();
            }
         }
      } catch (error) {
         const resourceIds = Array.isArray(values.resource)
            ? values.resource.map((r) => r.id)
            : values.resource
            ? [values.resource.id]
            : [];
         const equipmentIds = Array.isArray(values.equipment)
            ? values.equipment.map((e) => e.id)
            : values.equipment
            ? [values.equipment.id]
            : [];
         reportError(
            error as NotifiableError,
            `assignment-tearsheet__submit__${isCreateFlow ? "create" : "edit"}`,
            {
               "assignment submit details": {
                  isCreateFlow,
                  resourceType: resourceType ?? null,
                  resourceIds,
                  equipmentIds,
                  projectId: values.project?.id ?? null,
                  workflow: shouldOpenSendAlerts
                     ? WorkflowType.SUBMIT_AND_ALERT
                     : WorkflowType.SUBMIT_ONLY,
                  hasMultipleResources: resourceIds.length + equipmentIds.length > 1,
               },
            },
         );
         if (!isCreateFlow) {
            showToast.error(I18n.t("views.company.workforce_planning.assignment.edit_error"));
            resetForm(); // reset the form to initial values in case of error during edit flow
         } else {
            showToast.error(I18n.t("views.company.workforce_planning.assignment.create_error"));
         }
      } finally {
         setLoading(false);
      }
   }

   /* istanbul ignore next */
   const handleSubmitClick = async (
      submitForm: FormikProps<AssignmentFormValues>["submitForm"],
      validateForm: FormikProps<AssignmentFormValues>["validateForm"],
      values: AssignmentFormValues,
   ) => {
      // Validate the form first
      const errors = await validateForm();

      // If validation fails, display errors and don't proceed with submission
      if (Object.keys(errors).length > 0) {
         await submitForm(); // Triggers Formik's validation UI
         requestAnimationFrame(() => {
            focusFirstInvalidField();
         });
         return;
      }

      try {
         // Set flag to prevent handleClose from being called during submission
         setShouldOpenSendAlerts(true);
         // Set ref immediately so handleSubmit can see it
         isOpeningSendAlertsRef.current = true;

         // Submit the form — handleSubmit will dispatch open-send-assignment-alerts when this flag is set
         await submitForm();
      } catch (error) {
         // Log detailed error information - BugSnag
         {
            let resourceIds: string[] = [];
            if (Array.isArray(values.resource)) {
               resourceIds = values.resource.map((r) => r.id);
            } else if (values.resource) {
               resourceIds = [values.resource.id];
            }
            reportError(error as NotifiableError, "assignment-tearsheet__submit_alert_flow", {
               "assignment alert flow details": {
                  isCreateFlow,
                  resourceType: resourceType ?? null,
                  resourceIds,
                  projectId: values.project?.id ?? null,
               },
            });
         }
      } finally {
         // Reset flags to restore normal close behavior
         setShouldOpenSendAlerts(false);
         isOpeningSendAlertsRef.current = false;
      }
   };

   return (
      <Spinner loading={(supportDataLoading || loading) && assignmentCreationSupportData === null}>
         <Page>
            <Page.Main
               className="tearsheet-main"
               style={{
                  display: "flex",
                  flexDirection: "column",
               }}
            >
               <Page.Header>
                  <Page.Title>
                     <H1>
                        {isCreateFlow
                           ? I18n.t("views.company.workforce_planning.assignment.create_title")
                           : (() => {
                                if (resourceType === ResourceType.EQUIPMENT) {
                                   return I18n.t(
                                      "views.company.workforce_planning.assignment.edit_equipment_assignment",
                                   );
                                } else {
                                   return I18n.t(
                                      "views.company.workforce_planning.assignment.edit_labor_assignment",
                                   );
                                }
                             })()}
                     </H1>
                  </Page.Title>
               </Page.Header>
               <Form
                  view={formViewMode as FormView}
                  onSubmit={handleSubmit}
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  enableReinitialize
               >
                  {/* @ts-expect-error Core React Form uses Formik under the hood; this is valid implementation */}
                  {({
                     submitForm,
                     isSubmitting,
                     setFieldValue,
                     errors,
                     values,
                     validateForm,
                     submitCount,
                     isValid,
                  }: FormikProps<AssignmentFormValues>) => (
                     <>
                        <Page.Body style={{ width: "100%", marginBottom: "auto" }}>
                           <ValidationErrorAlert
                              submitCount={submitCount}
                              isValid={isValid}
                              errors={errors}
                           />
                           <Card style={{ marginBottom: "10px" }}>
                              <Box padding="md">
                                 <FlexList justifyContent="space-between" marginBottom="sm">
                                    <Title>
                                       <Title.Text>
                                          <H2>
                                             {I18n.t("views.company.workforce_planning.details")}
                                          </H2>
                                       </Title.Text>
                                    </Title>
                                    {formViewMode === ActionMode.READ && (
                                       <Button
                                          variant="secondary"
                                          onClick={() => setFormViewMode(ActionMode.UPDATE)}
                                          data-testid="edit-assignment"
                                       >
                                          {I18n.t("views.company.workforce_planning.edit")}
                                       </Button>
                                    )}
                                 </FlexList>
                                 <Form.Form>
                                    <Form.Row>
                                       <Form.Select
                                          colStart={1}
                                          colWidth={6}
                                          name="project"
                                          label={I18n.t("views.company.workforce_planning.project")}
                                          required
                                          options={formatProjectOptions(
                                             assignmentCreationSupportData?.data.project_options,
                                             I18n,
                                          )}
                                          searchComparator={projectSearchComparator}
                                          onSelect={({ item }) => {
                                             handleSelection(
                                                item,
                                                DropDownType.PROJECT,
                                                makeTotalHoursAssignment,
                                             );
                                             // Start from the current values so the user's edits survive the form reinitializing
                                             setInitialValues({
                                                ...values,
                                                project: item,
                                                category: null,
                                                subcategory: null,
                                             });
                                             setFieldValue("category", null);
                                             setFieldValue("subcategory", null);
                                             setTieredCategoryValue([]);
                                          }}
                                          placeholder={I18n.t(
                                             "views.company.workforce_planning.select_option",
                                          )}
                                          onClear={false}
                                       />
                                       <Form.TieredSelect
                                          colStart={7}
                                          colWidth={6}
                                          name="tieredCategory"
                                          label={I18n.t(
                                             "views.company.workforce_planning.category",
                                          )}
                                          options={tieredCategoryOptions ?? []}
                                          value={tieredCategoryValue}
                                          onChange={(sel: {
                                             value: CategoryTieredSelectOption[];
                                          }) => handleTieredCategoryChange(sel, setFieldValue)}
                                          onClear={() => handleTieredCategoryClear(setFieldValue)}
                                          getId={(tier: CategoryTieredSelectOption) => tier.id}
                                          getLabel={(tier: CategoryTieredSelectOption) =>
                                             tier.label
                                          }
                                          getGroupId={(tier: CategoryTieredSelectOption) =>
                                             tier.groupId
                                          }
                                          getNextGroupId={(tier: CategoryTieredSelectOption) =>
                                             tier.nextGroupId
                                          }
                                          selectableTiers
                                          loading={tieredCategoryLoading}
                                          disabled={!initialValues.project?.id}
                                       />
                                    </Form.Row>
                                    <Form.Row>
                                       {isCreateFlow ? (
                                          <Form.MultiSelect
                                             colStart={1}
                                             colWidth={6}
                                             name="resource"
                                             label={I18n.t(
                                                "views.company.workforce_planning.labor",
                                             )}
                                             options={formatOptions(
                                                assignmentCreationSupportData?.data
                                                   .resource_options ?? [],
                                             )}
                                             placeholder={I18n.t(
                                                "views.company.workforce_planning.assignment.labor_placeholder",
                                             )}
                                             onChange={(item) => {
                                                handleSelection(item, DropDownType.RESOURCE);
                                             }}
                                             required={false}
                                             disabled={resourceType === ResourceType.EQUIPMENT}
                                          />
                                       ) : (
                                          <Form.Select
                                             colStart={1}
                                             colWidth={6}
                                             name="resource"
                                             label={I18n.t(
                                                "views.company.workforce_planning.labor",
                                             )}
                                             options={formatOptions(
                                                assignmentCreationSupportData?.data
                                                   .resource_options ?? [],
                                             )}
                                             placeholder={I18n.t(
                                                "views.company.workforce_planning.assignment.labor_placeholder",
                                             )}
                                             onClear={() => {
                                                handleSelection(null, DropDownType.RESOURCE);
                                             }}
                                             onSelect={({ item }) => {
                                                handleSelection([item], DropDownType.RESOURCE);
                                             }}
                                             required={resourceType === ResourceType.LABOR}
                                             disabled={resourceType === ResourceType.EQUIPMENT}
                                          />
                                       )}
                                       <EquipmentSelectField
                                          multiple={isCreateFlow}
                                          getOptionGroup={(option) => option.type?.name ?? "Other"}
                                          label={I18n.t(
                                             "views.company.workforce_planning.equipment",
                                          )}
                                          companyId={
                                             procoreCompanyId != null ? Number(procoreCompanyId) : 1
                                          }
                                          groupIds={
                                             groupId === ALL_GROUPS_ID ? undefined : [groupId]
                                          }
                                          name="equipment"
                                          colStart={7}
                                          colWidth={6}
                                          onChange={(item) => {
                                             handleSelection(item, DropDownType.EQUIPMENT);
                                          }}
                                          required={resourceType === ResourceType.EQUIPMENT}
                                          optionLabel={2} // added to render equipment identification number along with name in the dropdown options
                                          disabled={
                                             !projectProcoreId ||
                                             resourceType === ResourceType.LABOR
                                          }
                                       />
                                    </Form.Row>
                                 </Form.Form>
                              </Box>
                           </Card>
                           {/** form for the schedule card */}
                           <ScheduleCard
                              I18n={I18n}
                              setFieldValue={setFieldValue}
                              assignmentCreationSupportData={assignmentCreationSupportData}
                              selectedWorkDays={selectedWorkDays}
                              setSelectedWorkDays={setSelectedWorkDays}
                              assignmentByTime={assignmentByTime}
                              setAssignmentByTime={setAssignmentByTime}
                              assignmentByAllocation={assignmentByAllocation}
                              setAssignmentByAllocation={setAssignmentByAllocation}
                              canViewAllStatuses={canViewAllStatuses}
                              currentAssignments={currentAssignments}
                              equipmentAssignmentsSchedules={equipmentAssignmentsSchedules}
                              values={values} // this will contain the form values
                              overTimeProps={{
                                 showOverTime: true,
                                 isOverTimeSelected,
                                 setIsOverTimeSelected,
                                 defaultOverTimeRates: getOverTimeRates(),
                                 errors,
                                 setTotalHours,
                              }}
                              setInitialValues={setInitialValues}
                              isCreateFlow={isCreateFlow}
                              parentStartDate={startDate}
                              parentEndDate={endDate}
                           />
                           {
                              /** form for the additional information/custom field card */
                              //TO DO - we are not showing custom fields card for equipment assignment as of now,
                              //will revisit once custom fields editing available for equipment assignment from backend
                              resourceType !== ResourceType.EQUIPMENT && (
                                 <CustomFieldsCard
                                    customFields={customFields ?? null}
                                    I18n={I18n}
                                    filterIntegrationOnly={filterIntegrationOnly}
                                 />
                              )
                           }
                           <AdditionalInstructionsCard />
                           <CommentsCard comments={commentList} />
                        </Page.Body>
                        {formViewMode !== ActionMode.READ && (
                           <Page.Footer>
                              <Box padding="md">
                                 <Flex justifyContent="space-between" alignItems="center">
                                    <Required showLabel />
                                    <FlexList space="sm">
                                       <Button
                                          variant="tertiary"
                                          disabled={isSubmitting}
                                          onClick={handleClose}
                                       >
                                          {I18n.t("views.company.workforce_planning.cancel")}
                                       </Button>
                                       {canSubmitAndAlert && (
                                          <Button
                                             variant="tertiary"
                                             disabled={isSubmitting}
                                             onClick={() =>
                                                handleSubmitClick(submitForm, validateForm, values)
                                             }
                                          >
                                             {getAssignmentActionText(
                                                values,
                                                isCreateFlow,
                                                I18n,
                                                WorkflowType.SUBMIT_AND_ALERT,
                                             )}
                                          </Button>
                                       )}
                                       <Button
                                          type="submit"
                                          disabled={isSubmitting}
                                          onClick={submitForm}
                                       >
                                          {getAssignmentActionText(
                                             values,
                                             isCreateFlow,
                                             I18n,
                                             WorkflowType.SUBMIT_ONLY,
                                          )}
                                       </Button>
                                    </FlexList>
                                 </Flex>
                              </Box>
                           </Page.Footer>
                        )}
                     </>
                  )}
               </Form>
            </Page.Main>
         </Page>
      </Spinner>
   );
};
